// Inicialização opcional do Sentry no app TV.
//
// - VITE_SENTRY_DSN vazio: no-op (não instala SDK, sem overhead).
// - VITE_SENTRY_DSN preenchido: instala @sentry/react em modo "thin".
//
// Lazy import pra que o bundle inicial NÃO inclua o SDK quando DSN vazio
// (caso comum em dev). Em prod com DSN, o chunk é carregado paralelamente
// ao app — não bloqueia first paint.
//
// 🔒 REDAÇÃO DE CREDENCIAL (cybersec P1): as integrações DEFAULT do Sentry gravam
// breadcrumbs de fetch/xhr AUTOMATICAMENTE, com a URL crua — ANTES de qualquer
// redação nossa. As URLs `.ts` do Xtream embutem user/pass no PATH (o probe do
// ramo native-ts E o loader do mpegts.js batem nelas), então sem isto o próximo
// evento capturado embarcaria a credencial do provedor pro DSN. `scrubBreadcrumbUrl`
// (via `beforeBreadcrumb`) e `scrubEventUrls` (via `beforeSend`, defense-in-depth)
// reduzem TODA URL de rede a `protocol//host` — perde o path no breadcrumb, mas
// host+método+status bastam pra debug e ZERO credencial sai da TV. É a mesma
// redação de `@app-iptv/shared:redactStreamUrl` (util único, sem duplicar).
//
// Escolha (origem-only pra TUDO, não desligar breadcrumbs): manter os breadcrumbs
// de rede tem valor de debug real (qual host, que status), e não dá pra distinguir
// com segurança um path de stream (com credencial) de um path de API (sem) — então
// redigir todos à origem é o único jeito robusto de garantir zero vazamento.

import { redactStreamUrl } from '@app-iptv/shared'

/** Shape mínimo de um breadcrumb do Sentry — só o que o scrub toca. Local de
 *  propósito: mantém as funções puras testáveis sem acoplar ao @sentry/react
 *  (que é lazy) e sem exigir o SDK no ambiente de teste. */
interface BreadcrumbLike {
  category?: string
  // Índice `[k]: unknown` casa com o `data?: {[key: string]: any}` do Breadcrumb
  // do Sentry E aceita breadcrumbs de rede sem `url` (fetch sem URL) sem o
  // weak-type check do TS reclamar.
  data?: { url?: unknown; [key: string]: unknown }
}

interface EventLike {
  // `request` tem `url` como prop nomeada (sem índice) — casa com o
  // `RequestEventData` do Sentry, que NÃO tem index signature.
  request?: { url?: unknown }
  breadcrumbs?: BreadcrumbLike[]
}

/**
 * 🔒 `beforeBreadcrumb`: redige a URL de um breadcrumb de fetch/xhr pra
 * `protocol//host`. Mutação in-place (o Sentry espera o mesmo objeto de volta).
 * Breadcrumbs que não são de rede passam intactos.
 */
export function scrubBreadcrumbUrl<B extends BreadcrumbLike>(breadcrumb: B): B {
  if (
    (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
    breadcrumb.data &&
    typeof breadcrumb.data.url === 'string'
  ) {
    breadcrumb.data.url = redactStreamUrl(breadcrumb.data.url)
  }
  return breadcrumb
}

/**
 * 🔒 `beforeSend` (defense-in-depth): redige a `request.url` do evento E varre os
 * breadcrumbs que ele carrega — pega qualquer URL que tenha escapado do
 * `beforeBreadcrumb` (ordem/versão do SDK) antes de sair pro DSN.
 */
export function scrubEventUrls<E extends EventLike>(event: E): E {
  if (event.request && typeof event.request.url === 'string') {
    event.request.url = redactStreamUrl(event.request.url)
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) scrubBreadcrumbUrl(b)
  }
  return event
}

let initialized = false

export async function initSentryIfConfigured(): Promise<void> {
  if (initialized) return
  const dsn = import.meta.env['VITE_SENTRY_DSN']
  if (!dsn) {
    // Silencioso em dev pra evitar log poluição.
    if (import.meta.env.PROD) {
      console.warn('[sentry] VITE_SENTRY_DSN não configurado — observability desativada')
    }
    return
  }

  try {
    const Sentry = await import('@sentry/react')
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // __APP_VERSION__ é injetado em build-time pelo Vite (define).
      release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
      // Captura erros não tratados + breadcrumbs. Sem profiling/replays
      // (pesados pra TV antiga).
      tracesSampleRate: 0.05,
      // Não envia PII.
      sendDefaultPii: false,
      // 🔒 Redige a credencial do Xtream que os breadcrumbs de fetch/xhr do SDK
      // gravariam crua (ver o cabeçalho). beforeBreadcrumb na captura;
      // beforeSend como defesa-em-profundidade no evento inteiro.
      beforeBreadcrumb: (breadcrumb) => scrubBreadcrumbUrl(breadcrumb),
      beforeSend: (event) => scrubEventUrls(event),
    })
    initialized = true
    console.warn('[sentry] inicializado com sucesso')
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    console.warn(`[sentry] falha ao inicializar (non-fatal): ${msg}`)
  }
}
